const EXPLICITLY_FORBIDDEN_PROTOCOLS = new Set([
  'javascript:',
  'data:',
  'blob:',
  'file:',
  'vbscript:',
]);

export interface UrlValidationOptions {
  /** Non-empty = only these hostnames are allowed (exact match). Empty/omitted = any host, protocol rules still apply. */
  allowedHosts?: string[];
  /** Defaults to true — reject plain http. */
  requireHttps?: boolean;
}

/**
 * Mirrors server/src/security/validateExternalUrl.ts — duplicated rather
 * than shared because frontend and backend are separate bundles/tsconfigs
 * (see server/src/contracts.ts for the same tradeoff). This is the ONE
 * place in the frontend allowed to decide whether a URL may ever become an
 * iframe `src`. A URL arriving in a Gemini/backend response is untrusted
 * input regardless of what the backend already checked — this function is
 * the actual gate at the point of DOM injection.
 */
export function isSafeExternalUrl(rawUrl: string, options: UrlValidationOptions = {}): boolean {
  let url: URL;
  try {
    url = new URL(rawUrl);
  } catch {
    return false;
  }

  if (EXPLICITLY_FORBIDDEN_PROTOCOLS.has(url.protocol)) return false;

  const requireHttps = options.requireHttps ?? true;
  if (requireHttps ? url.protocol !== 'https:' : !['https:', 'http:'].includes(url.protocol)) {
    return false;
  }

  if (
    options.allowedHosts &&
    options.allowedHosts.length > 0 &&
    !options.allowedHosts.includes(url.hostname)
  ) {
    return false;
  }

  return true;
}
